<template>
	<label :for="`input-${name}`" class="bbox-text-input-label">
		<span :class="[labelClasses, 'label']" v-if="!$slots.label && !!label">{{ label }}</span>
		<slot name="label" />
		<div class="input-wrapper" :class="{ autosize }">
			<div class="display-flex">
				<!--
          Evento emitido quando texto do input é alterado
          @property {value} valor do input
          @type{input}
        -->
				<!--
          Evento emitido quando input perde o foco
          @property {value} valor do input
          @type{change}
        -->
				<input
					ref="input"
					class="bbox-text-input"
					:class="[
						inputClasses,
						cleanButtonClicked || cleanButtonHasFocus ? 'bg-white-darker-3' : 'bg-white-darker-2'
					]"
					:id="`input-${name}`"
					:type="type"
					:value="value"
					:name="name"
					:placeholder="placeholder"
					:maxlength="maxlength"
					:disabled="disabled"
					@change="
						$event => {
							$emit('change', $event.target.value);
						}
					"
					@input="
						$event => {
							$emit('input', $event.target.value);
							updateSize();
						}
					"
					@keyup.enter="$emit('clicked', input)"
					v-focus="inputFocused"
					@focus="inputFocused = true"
					@blur="inputFocused = false"
					v-model="input"
				/>
				<button
					v-if="cleanable"
					ref="close-button"
					class="close-button"
					:class="[
						closeButtonClasses,
						inputFocused || cleanButtonClicked ? 'bg-white-darker-3' : 'bg-white-darker-2'
					]"
					:disabled="disabled"
					@mousedown="cleanButtonDown"
					@click="clean"
				>
					<b-icon ref="close-button-b-icon">
						<icon-clear ref="close-button-icon"></icon-clear>
					</b-icon>
				</button>
				<button
					:class="buttonClasses"
					@click="$emit('clicked', input)"
					@keyup.enter="$emit('clicked', input)"
					:disabled="disabled"
				>
					<b-icon v-if="icon">
						<component :is="icon" :class="iconClasses"></component>
					</b-icon>

					<slot v-else></slot>
				</button>
			</div>
		</div>
	</label>
</template>

<script>
import BIcon from './Icon.vue';
import { focus } from 'vue-focus';

export default {
	name: 'b-input-with-button',
	components: {
		BIcon
	},
	directives: { focus: focus },
	data() {
		return {
			input: '',
			inputFocused: false,
			cleanButtonClicked: false,
			cleanButtonHasFocus: false
		};
	},
	props: {
		/**
		 * Ícone do botão
		 */
		icon: {
			type: String,
			required: false
		},
		/**
		 * Classes para estilizar o input
		 */
		inputClasses: {
			type: String,
			default:
				'font-size-big ink-base placeholder-ink-lighter-1 padding-normal border-top border-bottom border-left border-white-darker-4 bg-focus-white-darker-3'
		},
		/**
		 * Classes para estilizar o botão com ícone
		 */
		buttonClasses: {
			type: String,
			default:
				'font-size-big ink-lighter-1 bold padding-normal border border-white-darker-4 border-focus-ink-lighter-1 bg-white-darker-2 bg-focus-white-darker-3'
		},
		/**
		 * Classes para estilizar o botão que limpa o input
		 */
		closeButtonClasses: {
			type: String,
			default:
				'font-size-big ink-lighter-1 bold padding-normal border-top border-bottom border-right border-white-darker-4 bg-focus-white-darker-3'
		},
		/**
		 * Classes para estilizar o ícone
		 */
		iconClasses: {
			type: String,
			default: 'font-size-xbig bold'
		},
		/**
		 * Classes para estilizar o label
		 */
		labelClasses: {
			type: String,
			default: 'font-size-big ink-base semi-bold margin-bottom-normal'
		},
		/**
		 * Name do InputWithButton
		 */
		name: {
			type: String,
			required: true
		},
		/**
		 * Texto do label do InputWithButton
		 */
		label: {
			type: String,
			default: ''
		},
		/**
		 * Valor inserido do InputWithButton
		 * @model
		 */
		value: {
			type: String,
			default: ''
		},
		/**
		 * Dica que descreve o valor a ser inserido no input
		 */
		placeholder: {
			type: String,
			default: 'Digite aqui...'
		},
		/**
		 * Especifica o número máximo de caracteres permitidos no input
		 */
		maxlength: {
			type: String,
			default: ''
		},
		/**
		 * Especifica o tipo do input, podendo ser: text, password, number e email
		 */
		type: {
			type: String,
			default: 'text',
			validator(val) {
				return val === 'text' || val === 'password' || val === 'number' || val === 'email';
			}
		},
		/**
		 * Se true, permite que InputWithButton ajuste seu tamanho automaticamente
		 */
		autosize: Boolean,
		/**
		 * Se true, InputWithButton fica desabilitado
		 */
		disabled: Boolean,
		/**
		 * Se true, conteúdo do input pode ser removido pelo botão clear, que passa a ser exibido
		 */
		cleanable: {
			type: Boolean,
			default: false
		}
	},
	methods: {
		updateSize() {
			if (this.autosize) {
				this.$refs.input.style.width = '0px';
				var realWidth = this.$refs.input.scrollWidth;
				if (this.$refs.input.value === '') realWidth += this.$refs.input.placeholder.length * 10;
				this.$refs.input.style.width = realWidth + 2 + 'px';
			}
		},
		clean() {
			this.input = '';
			this.cleanButtonClicked = false;
			/**
			 * Evento emitido quando usuário(a) clica no botão de limpar
			 */
			this.$emit('cleaned');
		},
		cleanButtonDown() {
			this.cleanButtonClicked = true;
		},
		iscleanButtonFocused(event) {
			const el = event.target;

			let cleanButton = this.$refs['close-button'];

			this.cleanButtonHasFocus = cleanButton === el ? true : false;
		},
		isCleanButtonNotFocused(event) {
			const el = event.target;

			let cleanButton = this.$refs['close-button'];

			if (cleanButton === el) this.cleanButtonHasFocus = false;
		}
	},
	created() {
		document.addEventListener('focusin', this.iscleanButtonFocused);
		document.addEventListener('focusout', this.isCleanButtonNotFocused);
	},
	beforeDestroy() {
		document.removeEventListener('focusin', this.iscleanButtonFocused);
		document.removeEventListener('focusout', this.isCleanButtonNotFocused);
	},
	mounted() {
		this.updateSize();
	},
	updated() {
		this.updateSize();
	}
};
</script>

<style scoped lang="scss">
@import "../css/_variables.scss";
label,
label * {
	box-sizing: border-box;
}
label {
	display: block;
}
.input-wrapper {
	position: relative;
	width: 100%;
	display: inline-block; //to counter margin merging
	&.autosize {
		width: auto;
		display: inline-block;
		input {
			width: auto;
		}
	}
}
input {
	border-top-left-radius: 5px;
	border-bottom-left-radius: 5px;
	border-right: none;
	display: block;
	width: 100%;
	position: relative;
	z-index: 1;
	outline: none;
	&:disabled {
		cursor: not-allowed !important;
		color: map-get($colors, 'ink-lighter-2')
	}
}
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
	-webkit-appearance: none;
	margin: 0;
}
label span.label {
	display: block;
	text-align: left;
}
button {
	cursor: pointer;
	border-left: none;
	border-top-right-radius: 5px;
	border-bottom-right-radius: 5px;
	&:disabled {
		cursor: not-allowed !important;
	}
}
.close-button {
	border-radius: 0px;
}
</style>

<docs>
  Input com botão. O botão contém um ícone
  ```js 

    <b-input-with-button
      name="search"
      icon="icon-search"
      placeholder="Pesquisar Prolancers"
      cleanable
      @clicked="searchProlancers"
      @cleaned="searchProlancers('')"
    ></b-input-with-button>

     <b-input-with-button
      name="search"
      icon="icon-search"
      placeholder="Pesquisar Prolancers"
      @clicked="searchProlancers"
      @cleaned="searchProlancers('')"
    ></b-input-with-button>

  ```
</docs>
